import { useEffect, useState } from "react"
import { detectMetaApp, type MetaAppInfo } from "../utils/metaAppRedirect"
import { useDevice } from "../contexts/device"

/**
 * Hook to detect Meta apps and provide redirect functionality
 * @returns MetaAppInfo and utility functions
 */
export function useMetaAppDetection() {
  const [metaAppInfo, setMetaAppInfo] = useState<MetaAppInfo | null>(null)
  const [isClient, setIsClient] = useState(false)
  const device = useDevice()

  useEffect(() => {
    setIsClient(true)

    if (typeof window !== "undefined") {
      const userAgent = navigator.userAgent
      const info = detectMetaApp(userAgent)
      setMetaAppInfo(info)
    }
  }, [])

  const openInExternalBrowser = (url?: string) => {
    if (typeof window === "undefined" || !metaAppInfo) {
      return
    }

    const targetUrl = url || window.location.href

    if (metaAppInfo.isMetaApp) {
      // Try multiple methods to open in external browser

      // Method 1: Intent URL for Android
      if (/android/i.test(navigator.userAgent)) {
        const baseUrl = targetUrl.replace(/^https?:\/\//, "")
        const intentUrl = `intent://${baseUrl}#Intent;scheme=https;package=com.android.chrome;package=com.chrome.beta;package=org.mozilla.firefox;package=com.microsoft.emmx;package=com.opera.browser;package=com.sec.android.app.sbrowser;S.browser_fallback_url=${encodeURIComponent(targetUrl)};end;`

        window.location.href = intentUrl

        // Fallback after 2 seconds
        setTimeout(() => {
          window.open(targetUrl, "_blank", "noopener,noreferrer")
        }, 2000)
      } else {
        // Method 2: For iOS, try opening in new tab/window
        const opened = window.open(targetUrl, "_blank", "noopener,noreferrer")

        // If popup was blocked, show instructions
        if (!opened || opened.closed || typeof opened.closed === "undefined") {
          alert(
            "Please manually copy this URL and paste it in your browser: " +
              targetUrl
          )
        }
      }
    } else {
      // Not a Meta app, just open normally
      window.open(targetUrl, "_blank", "noopener,noreferrer")
    }
  }

  const copyUrlToClipboard = (url?: string) => {
    if (typeof window === "undefined") {
      return Promise.reject("Not in browser")
    }

    const targetUrl = url || window.location.href

    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(targetUrl)
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = targetUrl
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand("copy")
        document.body.removeChild(textArea)
        return Promise.resolve()
      } catch (err) {
        document.body.removeChild(textArea)
        return Promise.reject(err)
      }
    }
  }

  return {
    metaAppInfo,
    isClient,
    isMetaApp: metaAppInfo?.isMetaApp || false,
    isFacebookApp: metaAppInfo?.isFacebookApp || false,
    isInstagramApp: metaAppInfo?.isInstagramApp || false,
    shouldRedirect: metaAppInfo?.shouldRedirect || false,
    openInExternalBrowser,
    copyUrlToClipboard,
  }
}
